import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouteContext } from '@tanstack/react-router';
import { Button, Empty, Flex, message, Modal, Result, Tag, Typography } from 'antd';
import { RotateCw, Unplug } from 'lucide-react';
import { useCallback } from 'react';

import {
  getListConnectedAppsQueryKey,
  getListMyConnectedAppsQueryKey,
  useListConnectedApps,
  useListMyConnectedApps,
  useRevokeConnectedApp,
} from '../../../../api';
import { usePermissions } from '../../../../modules/account/hooks';
import { ConnectApps } from '../../../../modules/oauth/components/connect-apps';
import { SettingsLayoutContent } from '../../../../ui/components/layout';
import { List } from '../../../../ui/components/list';
import { Loader } from '../../../../ui/components/loader';
import { Record } from '../../../../ui/components/record';
import { formatDate } from '../../../../ui/utils/format-date';

import type { ListConnectedAppsResponseDtoItemsItem } from '../../../../api/generated/model';

/**
 * Authorized apps — external clients (Claude and other MCP clients) that hold an
 * OAuth grant against this workspace.
 *
 * Deliberately NOT the same page as Connected Apps: that one is about mailboxes
 * K3X reaches *out* to (Gmail, Outlook). This is the opposite direction — who
 * can reach *in*, and with which permissions.
 */

/**
 * What a scope means to someone who has never read the OAuth spec. Must cover
 * every scope in the backend’s INTEGRATION_SCOPES (apps/backend/src/modules/oauth/scopes.ts);
 * an unknown one falls back to its raw name, which is what a reader should never see.
 */
const SCOPE_LABELS: Record<string, string> = {
  'records.read': 'Read records',
  'records.write': 'Create and edit records',
  'deals.read': 'Read deals',
  'deals.write': 'Create and edit deals',
  'communications.read': 'Read emails, calls and meetings',
  'agents.read': 'Read AI agents',
  'agents.write': 'Create and edit AI agents',
  offline_access: 'Stay connected',
};

/**
 * Who authorized a connection, for the organization-wide list. Name first, then
 * the email, and only then the raw uuid — a grant outlives the member it belonged
 * to, so the id is the last resort rather than the default.
 */
const ownerLabel = (app: ListConnectedAppsResponseDtoItemsItem): string | null => {
  if (app.userName) {
    return app.userName;
  }
  if (app.userEmail) {
    return app.userEmail;
  }
  return app.userId ? `member ${app.userId}` : null;
};

const SettingsAuthorizedAppsRoute = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useRouteContext({ from: '/_authenticated/_app' });
  const { can } = usePermissions();
  const organizationId = selectedOrganization.id;
  const canManageEveryone = can('settings', 'manageMembershipsAndRoles');

  const mine = useListMyConnectedApps(organizationId);
  // Only fetched for someone who may see the whole organization; the endpoint
  // 403s otherwise, and a rejected request would surface as a page-level error.
  const all = useListConnectedApps(organizationId, { query: { enabled: canManageEveryone } });

  const { mutate: revoke, isPending: isRevoking } = useRevokeConnectedApp({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListMyConnectedAppsQueryKey(organizationId) });
        queryClient.invalidateQueries({ queryKey: getListConnectedAppsQueryKey(organizationId) });
        message.success('Access revoked');
      },
      onError: () => {
        message.error('Failed to revoke access. Please try again.');
      },
    },
  });

  const confirmRevoke = useCallback(
    (app: ListConnectedAppsResponseDtoItemsItem) => {
      Modal.confirm({
        title: `Revoke access for ${app.clientName ?? 'this application'}?`,
        content:
          'It loses access immediately and stops working until someone connects it again. Anything it already created stays.',
        okText: 'Revoke access',
        okButtonProps: { danger: true },
        onOk: () => revoke({ organizationId, grantId: app.grantId }),
      });
    },
    [organizationId, revoke],
  );

  const renderApp = (showOwner: boolean) => (app: ListConnectedAppsResponseDtoItemsItem) => {
    const owner = showOwner ? ownerLabel(app) : null;

    return {
      key: app.grantId,
      reactNode: (
        <Record
          left={<Unplug size={24} />}
          title={app.clientName ?? 'Unnamed application'}
          description={
            <Flex vertical gap={4}>
              <Flex gap={4} wrap>
                {app.scopes.map((scope) => (
                  <Tag key={scope}>{SCOPE_LABELS[scope] ?? scope}</Tag>
                ))}
              </Flex>
              <Typography.Text type="secondary">
                {`Connected ${formatDate(app.connectedAt)}`}
                {owner ? ` · ${owner}` : ''}
              </Typography.Text>
            </Flex>
          }
          right={
            <Button danger loading={isRevoking} onClick={() => confirmRevoke(app)}>
              Revoke
            </Button>
          }
        />
      ),
    };
  };

  if (mine.status === 'pending' || (canManageEveryone && all.status === 'pending')) {
    return <Loader />;
  }

  if (mine.status === 'error') {
    return (
      <Result
        status="error"
        title="Failed to load!"
        subTitle="Please try again. If this keeps happening, check your connection or contact support."
        extra={
          <Button icon={<RotateCw size="1em" />} onClick={() => mine.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }

  const myApps = mine.data?.data?.items ?? [];
  const everyApp = all.data?.data?.items ?? [];
  // The caller's own connections already have their own section above.
  const othersApps = everyApp.filter((app) => !myApps.some((own) => own.grantId === app.grantId));

  return (
    <SettingsLayoutContent
      title="Authorized apps"
      description="Applications you have given access to this workspace, and what each one may do."
    >
      <Flex vertical gap={32}>
        <Flex vertical gap={12}>
          <Typography.Title level={5}>Connect an app</Typography.Title>
          <ConnectApps />
        </Flex>

        <Flex vertical gap={12}>
          <Typography.Title level={5}>Your connections</Typography.Title>
          {myApps.length === 0 ? (
            <Empty
              description="You have not connected any applications"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <List items={myApps} renderItem={renderApp(false)} />
          )}
        </Flex>

        {canManageEveryone && (
          <Flex vertical gap={12}>
            <Typography.Title level={5}>Other members’ connections</Typography.Title>
            {all.status === 'error' ? (
              // Never fall through to the empty state on a failed query: "nobody
              // has connected anything" and "we could not find out" look identical
              // to a reader, and only one of them means there is nothing to revoke.
              <Result
                status="error"
                title="Could not load the organization’s connections"
                subTitle="Your own connections above are unaffected. Please try again."
                extra={
                  <Button icon={<RotateCw size="1em" />} onClick={() => all.refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : othersApps.length === 0 ? (
              <Empty
                description="No other member has connected an application"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <List items={othersApps} renderItem={renderApp(true)} />
            )}
          </Flex>
        )}
      </Flex>
    </SettingsLayoutContent>
  );
};

export const Route = createFileRoute('/_authenticated/_app/settings/authorized-apps')({
  component: SettingsAuthorizedAppsRoute,
});
